import { Component, computed, input, signal } from '@angular/core';
import type { Signal } from '@angular/core';
import { clientLogger } from '@mb/common/client';

const scaleValues = [1, 2, 3, 4, 5] as const;

export type BottomToastScale = (typeof scaleValues)[number];

/** Semantic message type for toast styling. Default is 'normal'; 'info' uses the same style as normal. */
export type BottomToastMessageType = 'normal' | 'info' | 'warning' | 'error';

export interface BottomToastContext {
  message?: string;
  /** Semantic type: normal (default), info, warning, error */
  type?: BottomToastMessageType;
  size?: BottomToastScale;
  variant?: BottomToastScale;
  durationSeconds?: BottomToastScale;
  showClose?: boolean;
  actionLabel?: string;
  action?: () => void | Promise<void>;
  /** Optional second action (e.g. block + checkout on the same toast). */
  secondaryActionLabel?: string;
  secondaryAction?: () => void | Promise<void>;
  onClose?: () => void;
}

const normalizeScale = (
  value: number | undefined,
  fallback: BottomToastScale,
): BottomToastScale =>
  scaleValues.includes(value as BottomToastScale)
    ? (value as BottomToastScale)
    : fallback;

const sizeClasses: Record<BottomToastScale, string> = {
  1: 'w-[min(360px,calc(100%-2rem))]',
  2: 'w-[min(440px,calc(100%-2rem))]',
  3: 'w-[min(520px,calc(100%-2rem))]',
  4: 'w-[min(640px,calc(100%-2rem))]',
  5: 'w-[min(720px,calc(100%-2rem))]',
};

const variantClasses: Record<BottomToastScale, string> = {
  1: 'bg-primary-600 text-white',
  2: 'bg-secondary text-secondary-foreground',
  3: 'bg-emerald-600 text-white',
  4: 'bg-amber-500 text-white',
  5: 'bg-destructive text-white',
};

const messageTypeClasses: Record<BottomToastMessageType, string> = {
  normal: 'bg-primary-600 text-white',
  info: 'bg-primary-600 text-white',
  warning: 'bg-amber-500 text-white',
  error: 'bg-destructive text-white',
};

@Component({
  selector: 'app-bottom-toast',
  standalone: true,

  template: `
    <div class="pointer-events-auto w-full">
      <div
        class="flex items-center justify-between gap-4 px-6 py-4 rounded-lg shadow-xl min-w-[640px] max-w-[90vw] relative"
        [class]="toastClasses()"
        role="status"
        aria-live="polite"
      >
        <span
          class="text-base font-medium text-white flex-1 text-center flex flex-wrap items-center justify-center gap-x-1 gap-y-1"
        >
          <span>{{ message() }}</span>
          @if (hasPrimaryAction()) {
            <button
              type="button"
              [disabled]="isLoading()"
              class="underline cursor-pointer hover:no-underline disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center gap-1.5"
              (click)="onPrimaryAction()"
            >
              @if (isLoading() && activeAction() === 'primary') {
                <svg
                  class="w-3.5 h-3.5 animate-spin"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    class="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    stroke-width="4"
                  ></circle>
                  <path
                    class="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  ></path>
                </svg>
              }
              {{ actionLabel() }}
            </button>
          }
          @if (hasPrimaryAction() && hasSecondaryAction()) {
            <span class="text-white/70" aria-hidden="true">·</span>
          }
          @if (hasSecondaryAction()) {
            <button
              type="button"
              [disabled]="isLoading()"
              class="underline cursor-pointer hover:no-underline disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center gap-1.5"
              (click)="onSecondaryAction()"
            >
              @if (isLoading() && activeAction() === 'secondary') {
                <svg
                  class="w-3.5 h-3.5 animate-spin"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    class="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    stroke-width="4"
                  ></circle>
                  <path
                    class="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  ></path>
                </svg>
              }
              {{ secondaryActionLabel() }}
            </button>
          }
        </span>
        @if (showClose()) {
          <button
            type="button"
            class="absolute right-4 top-1/2 -translate-y-1/2 inline-flex h-6 w-6 items-center justify-center text-white hover:text-white/80 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/50 rounded z-10"
            (click)="onClose()"
            aria-label="Close"
          >
            <svg
              class="w-4 h-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        }
      </div>
    </div>
  `,
})
export class BottomToastComponent {
  public readonly messageInput = input<string>('');
  /** Semantic message type: normal | info | warning | error. When set, overrides variantInput for styling. */
  public readonly typeInput = input<BottomToastMessageType>('normal');
  public readonly sizeInput = input<BottomToastScale>(4);
  public readonly variantInput = input<BottomToastScale>(1);
  public readonly showCloseInput = input<boolean>(true);
  public readonly actionLabelInput = input<string | undefined>(undefined);
  public readonly actionInput = input<(() => void | Promise<void>) | undefined>(
    undefined,
  );
  public readonly secondaryActionLabelInput = input<string | undefined>(
    undefined,
  );
  public readonly secondaryActionInput = input<
    (() => void | Promise<void>) | undefined
  >(undefined);
  public readonly closeInput = input<(() => void) | undefined>(undefined);

  private readonly _isLoading = signal(false);
  private readonly _activeAction = signal<'primary' | 'secondary' | null>(null);

  public readonly message: Signal<string> = computed(() => this.messageInput());
  public readonly messageType: Signal<BottomToastMessageType> = computed(() =>
    this.typeInput(),
  );
  public readonly size: Signal<BottomToastScale> = computed(() =>
    normalizeScale(this.sizeInput(), 4),
  );
  public readonly variant: Signal<BottomToastScale> = computed(() =>
    normalizeScale(this.variantInput(), 1),
  );
  public readonly showClose: Signal<boolean> = computed(() =>
    this.showCloseInput(),
  );
  public readonly actionLabel: Signal<string | undefined> = computed(() =>
    this.actionLabelInput(),
  );
  public readonly action: Signal<(() => void | Promise<void>) | undefined> =
    computed(() => this.actionInput());
  public readonly secondaryActionLabel: Signal<string | undefined> = computed(
    () => this.secondaryActionLabelInput(),
  );
  public readonly secondaryAction: Signal<
    (() => void | Promise<void>) | undefined
  > = computed(() => this.secondaryActionInput());

  public readonly hasPrimaryAction = computed(
    () => Boolean(this.actionLabel()) && typeof this.action() === 'function',
  );
  public readonly hasSecondaryAction = computed(
    () =>
      Boolean(this.secondaryActionLabel()) &&
      typeof this.secondaryAction() === 'function',
  );

  public readonly activeAction = this._activeAction.asReadonly();

  /** Size + style from message type (normal/info/warning/error) */
  public readonly toastClasses = computed(
    () =>
      `${sizeClasses[this.size()]} ${messageTypeClasses[this.messageType()]}`,
  );

  public readonly isLoading = this._isLoading.asReadonly();

  public async onPrimaryAction(): Promise<void> {
    await this.#runAction('primary', this.action());
  }

  public async onSecondaryAction(): Promise<void> {
    await this.#runAction('secondary', this.secondaryAction());
  }

  async #runAction(
    which: 'primary' | 'secondary',
    fn: (() => void | Promise<void>) | undefined,
  ): Promise<void> {
    if (!fn) return;

    this._activeAction.set(which);
    this._isLoading.set(true);
    try {
      await Promise.resolve(fn());
      this.onClose();
    } catch (error) {
      clientLogger.error('Action failed', {
        error: error instanceof Error ? error : new Error(String(error)),
      });
      this._isLoading.set(false);
      this._activeAction.set(null);
    }
  }

  public onClose(): void {
    this.closeInput()?.();
  }
}
